import { useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CircleNotch as Loader2,
  Warning as AlertTriangle,
  Scales as Scale,
} from '@phosphor-icons/react';
import { useVerifyMagicLink } from '@/features/auth/magicLink';

/**
 * Magic-link landing page — /solicitor/verify?token=...
 * Immediately exchanges the token for a session; on success the hook
 * redirects to /solicitor. On failure we show a recovery path.
 */
export default function SolicitorVerifyPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const verify = useVerifyMagicLink();
  const fired = useRef(false);

  useEffect(() => {
    if (!token || fired.current) return;
    fired.current = true;
    verify.mutate(token);
  }, [token, verify]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-8 text-center shadow-sm">
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-md bg-brand-600 text-white">
          <Scale className="h-6 w-6" weight="duotone" />
        </div>

        {!token && (
          <>
            <AlertTriangle className="mx-auto mb-3 h-6 w-6 text-amber-500" weight="fill" />
            <h1 className="mb-2 text-lg font-semibold text-foreground">
              Missing sign-in link
            </h1>
            <p className="mb-4 text-sm text-muted-foreground">
              This page needs a valid sign-in token. Request a new link to continue.
            </p>
            <Link
              to="/solicitor/login"
              className="inline-flex h-10 items-center justify-center rounded-md bg-brand-600 px-4 text-sm font-medium text-white hover:bg-brand-700"
            >
              Request a new link
            </Link>
          </>
        )}

        {token && verify.isPending && (
          <>
            <Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin text-brand-600 dark:text-brand-400" />
            <h1 className="mb-1 text-lg font-semibold text-foreground">Signing you in…</h1>
            <p className="text-sm text-muted-foreground">One moment.</p>
          </>
        )}

        {token && verify.isError && (
          <>
            <AlertTriangle className="mx-auto mb-3 h-6 w-6 text-red-500" weight="fill" />
            <h1 className="mb-2 text-lg font-semibold text-foreground">Link didn't work</h1>
            <p className="mb-4 text-sm text-muted-foreground">
              {verify.error instanceof Error ? verify.error.message : 'Try again.'}
            </p>
            <Link
              to="/solicitor/login"
              className="inline-flex h-10 items-center justify-center rounded-md bg-brand-600 px-4 text-sm font-medium text-white hover:bg-brand-700"
            >
              Request a new link
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
